// Session 23: "Copy as text" for the agency SEO checklist. Plain text for
// pasting into an email (e.g. Landry's list going to Dave): a header line
// (client, analysis date, item count), then the visible tasks grouped by
// bucket (Session 24) and then by page,
// each with title, what to fix, how and done when. Hidden tasks are left out.

const pathOf = (t) => {
  const p = String(t.issue_key || '').split('|')[0];
  return p || t.page_url || '';
};

function howLines(how) {
  if (!how) return [];
  const h = typeof how === 'string' ? (() => { try { return JSON.parse(how); } catch { return null; } })() : how;
  const steps = (h && Array.isArray(h.steps)) ? h.steps : [];
  return steps.map((s, i) => `     ${i + 1}. ${s}`);
}

export function seoChecklistText({ clientName, baselineDate, tasks }) {
  // Session 24: grouped by bucket (plain title, in bucket order), then by page.
  // Deferred items never reach this (the caller passes what the client sees).
  const visible = (tasks || []).filter((t) => !t.hidden && !t.deferred)
    .slice().sort((a, b) => (a.bucket_sort ?? 9999) - (b.bucket_sort ?? 9999) || (a.page_rank ?? 0) - (b.page_rank ?? 0)
      || (a.sort ?? 0) - (b.sort ?? 0));
  const buckets = new Map();
  for (const t of visible) {
    const b = t.bucket_title || String(t.issue_key || '').split('|')[1] || 'Other';
    if (!buckets.has(b)) buckets.set(b, new Map());
    const pages = buckets.get(b);
    const key = pathOf(t) === '*' ? '*' : (t.page_url || pathOf(t));
    if (!pages.has(key)) pages.set(key, []);
    pages.get(key).push(t);
  }
  const head = `SEO checklist for ${clientName || 'the client'}`
    + (baselineDate ? `, from the analysis of ${baselineDate}` : '')
    + ` (${visible.length} item${visible.length === 1 ? '' : 's'})`;
  const out = [head, ''];
  for (const [title, pages] of buckets) {
    const n = [...pages.values()].reduce((k, l) => k + l.length, 0);
    out.push(`== ${title} (${n}) ==`, '');
    for (const [key, list] of pages) {
      out.push(key === '*' ? 'Site-wide' : `Page: ${key}`);
      for (const t of list) {
        out.push(`  - ${t.title}`);
        if (t.fix) out.push(`    What to fix: ${t.fix}`);
        const how = howLines(t.how_to);
        if (how.length) { out.push('    How:'); out.push(...how); }
        if (t.done_when) out.push(`    Done when: ${t.done_when}`);
        out.push('');
      }
    }
  }
  return out.join('\n').replace(/\n+$/, '\n');
}

export async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    let ok = false;
    try { ok = document.execCommand('copy'); } catch { ok = false; }
    document.body.removeChild(ta);
    return ok;
  }
}
